<template>
  <transition name="fadeInUp">
    <v-dialog
      width="fit-content"
      max-width="600"
      elevation="0"
      v-model="modalIsOpen"
      @click:outside="toggleModal"
      persistent
    >
      <div class="wallet_update_modal">
        <v-card class="d-flex justify-center" color="transparent" elevation="0">
          <h4 class="modal_title">{{ $t("TITLES.updateWallet") }}</h4>

          <form @submit.prevent="validateFormInputs">
            <div class="row">
              <!-- Start:: Current Balance Display -->
              <div class="col-12 mb-4" v-if="currentBalance !== null">
                <div class="balance_display">
                  <span class="balance_label"
                    >{{ $t("PLACEHOLDERS.currentBalance") }}:</span
                  >
                  <span class="balance_value"
                    >{{ currentBalance }} {{ $t("PLACEHOLDERS.riyal") }}</span
                  >
                </div>
              </div>
              <!-- End:: Current Balance Display -->

              <!-- Start:: Amount Input -->
              <base-input
                col="12"
                type="number"
                :placeholder="$t('PLACEHOLDERS.amount')"
                v-model.number="data.amount"
                step="0.01"
                required
              >
                <template slot="hint">
                  <small class="text-muted">
                    {{ $t("PLACEHOLDERS.amountHint") }}
                  </small>
                </template>
              </base-input>
              <!-- End:: Amount Input -->

              <!-- Start:: Description Input -->
              <base-input
                col="12"
                type="textarea"
                rows="3"
                :placeholder="$t('PLACEHOLDERS.description')"
                v-model.trim="data.description"
                :maxlength="500"
              />
              <!-- End:: Description Input -->

              <!-- Start:: Submit Button Wrapper -->
              <div class="btn_wrapper d-flex justify-content-center gap-2">
                <base-button
                  class="mt-2"
                  styleType="primary_btn"
                  :btnText="$t('BUTTONS.save')"
                  :isLoading="isWaitingRequest"
                  :disabled="isWaitingRequest"
                />

                <base-button
                  class="mt-2"
                  styleType="outline_btn"
                  :btnText="$t('BUTTONS.cancel')"
                  :disabled="isWaitingRequest"
                  @fireClick="toggleModal"
                />
              </div>
              <!-- End:: Submit Button Wrapper -->
            </div>
          </form>
        </v-card>
      </div>
    </v-dialog>
  </transition>
</template>

<script>
export default {
  name: "UpdateWalletModal",

  props: {
    modalIsOpen: {
      type: Boolean,
      required: true,
    },
    userId: {
      type: [Number, String],
      required: true,
    },
    currentBalance: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Data Collection
      data: {
        amount: null,
        description: "",
      },
      // End:: Data Collection
    };
  },

  methods: {
    // Start:: Toggle Modal
    toggleModal() {
      this.$emit("toggleModal");
      this.resetForm();
    },
    // End:: Toggle Modal

    // Start:: Reset Form
    resetForm() {
      this.data.amount = null;
      this.data.description = "";
      this.isWaitingRequest = false;
    },
    // End:: Reset Form

    // Start:: Validate Form Inputs
    validateFormInputs() {
      // Validate amount
      if (!this.data.amount || this.data.amount === 0) {
        this.$message.error(
          this.$t("VALIDATION.amountRequired") ||
            "Amount is required and cannot be zero"
        );
        return;
      }

      // Check if amount is a valid number
      if (isNaN(this.data.amount)) {
        this.$message.error(
          this.$t("VALIDATION.amountMustBeNumber") ||
            "Amount must be a valid number"
        );
        return;
      }

      this.isWaitingRequest = true;
      this.submitForm();
    },
    // End:: Validate Form Inputs

    // Start:: Submit Form
    async submitForm() {
      try {
        // Determine transaction type based on amount sign
        const transactionType = this.data.amount > 0 ? "credit" : "debit";
        const absoluteAmount = Math.abs(this.data.amount);

        const requestData = {
          amount: absoluteAmount,
          type: transactionType,
          description: this.data.description,
        };

        await this.$axios({
          method: "PATCH",
          url: `service-requesters/${this.userId}/wallet`,
          data: requestData,
        });

        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.walletUpdatedSuccessfully"));
        this.$emit("walletUpdated");
        this.toggleModal();
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Submit Form
  },

  watch: {
    // Reset form when modal is opened
    modalIsOpen(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.wallet_update_modal {
  .modal_title {
    color: #e1423d;
    font-weight: 600;
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
    text-align: center;
  }

  .balance_display {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    .balance_label {
      color: rgba(255, 255, 255, 0.9);
      font-size: 14px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    .balance_value {
      color: #ffffff;
      font-size: 28px;
      font-weight: 700;
    }
  }

  .text-muted {
    display: block;
    margin-top: 5px;
    color: #6c757d;
    font-style: italic;
  }

  ::v-deep .v-card {
    padding: 2rem;
    border-radius: 15px;
    background: white !important;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
  }

  .btn_wrapper {
    width: 100%;
    margin-top: 1rem;

    button {
      flex: 1;
      min-width: 120px;
    }
  }
}
</style>
